import { useEffect, useState } from "react";
import {
	Grid,
	Dialog,
	Box,
	DialogActions,
	Button,
	Typography,
	DialogTitle,
	IconButton,
    TextField,
    MenuItem,
    CircularProgress,
} from "@material-ui/core";
import { KeyboardDatePicker  } from "@material-ui/pickers";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleOutline from "@material-ui/icons/CheckCircleOutlineSharp";
import BlockIcon from "@material-ui/icons/Block"
import pdfIcon from '../../../../assets/icons/pdf.svg';
import styles from "./GeneratePRNModal.module.css";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment'
import { getSessionStorage, setSessionStorage } from "../../../../utils/storage";
import { useDispatch, useSelector } from "react-redux";
import { getContributionsAction, getMembershipTypesAction, getPrnDataAction } from "../../../../redux/modules/SSSPRN/prnGenerator";
import { TerminalUtil } from "../../../../utils/checkSession";
import { Alert } from "@material-ui/lab";

const validationSchema = Yup.object({
    ssId: Yup.string().matches(/^\d+$/, 'SS ID No. should be numeric only')
    .required('SS ID No. is required')
    .min(10, 'SS ID No. must be 10 digits')
    .max(10, 'SS ID No. must be 10 digits'),
    membershipType: Yup.string().required('Payment Type is required'),
    applicablePeriodFrom: Yup.date().required('Applicable Period From is Required'),
    applicablePeriodTo: Yup.date().required('Applicable Period To is Required')
        .test(
            'end-after-start',
            'Applicable Period To must be after Applicable Period From',
            function (value) {
                const { applicablePeriodFrom } = this.parent;
                return (
                    value &&
                    applicablePeriodFrom &&
                    moment(value).isSameOrAfter(moment(applicablePeriodFrom), 'month')
                );
            }
        ),
    contribution: Yup.number().required('Monthly Contribution is required'),
});

const GeneratePRNModal = ({open, handleOnCloseModal}) => {
    let dispatch = useDispatch();
    const [openModal, setOpenModal] = useState(open);
    const { membershipTypes, contributions, prnData, requestType, isLoading, error } = useSelector((state) => state.prnGenerator);
    const storedMembershipTypes = getSessionStorage("membershipTypes")
    const { type } = useSelector((state) => state.walletBalance);
    const terminal = TerminalUtil.get();
    const [membershipTypeOptions, setMembershipTypeOptions] = useState([])
    const [contributionOptions, setContributionOptions] = useState([])
    const [prnDataGenerate , setPRNDataGenerate] = useState(null)
    const [errorData , setErrorData] = useState(null)
    const storedLatestValidFrom = getSessionStorage("SSSPRNLatestValidFrom");

    const tpaId = terminal.name
    const walletType = type.toUpperCase()

    useEffect(()=>{
        if(open){
            setOpenModal(true)
            resetForm()
            getMembershipTypeList()
        }
    },[open])

    const getMembershipTypeList = () =>{
        if(!storedMembershipTypes){
            dispatch(getMembershipTypesAction({tpa_id : tpaId, wallet_type : walletType}));
        }else{
            setMembershipTypeOptions(storedMembershipTypes)
        }
    }

    useEffect(()=>{
        if((membershipTypes && membershipTypes.length > 0)){
            setMembershipTypeOptions(membershipTypes)
            setSessionStorage("membershipTypes",membershipTypes)
        }

        if(contributions && contributions.length > 0){
            setContributionOptions(contributions)
        }

        if(prnData){
            setPRNDataGenerate(prnData)
        }

        if(error){
            setErrorData(error)
        }

    },[membershipTypes, contributions, prnData, error])

    const closeModalAndReset = () => {
		handleOnCloseModal(null);
        setOpenModal(false)
        resetForm()
        closeErrorAlert(null)
	};

    const formik = useFormik({
        initialValues: {
            ssId: '',
            membershipType: '',
            applicablePeriodFrom: "",
            applicablePeriodTo: "",
            contribution: '',
        },
        validationSchema,
        onSubmit: (values) => {
            handleGeneratePRN(values)
        },
        validateOnChange : true,
        validateOnBlur : true
    });

    const resetForm = () => {
        formik.resetForm()
        formik.setValues({
            ssId: '',
            membershipType: '',
            applicablePeriodFrom: null,
            applicablePeriodTo: null,
            contribution: '',
        })
        setPRNDataGenerate(null)
        setErrorData(null)
    }

    const handleContributions = (applicable_period_from, membership_type_id) =>{
        setErrorData(null)
        setContributionOptions([])
        dispatch(getContributionsAction({
            tpa_id : tpaId, 
            wallet_type : walletType, 
            membership_type_id: membership_type_id, 
            applicable_period_from: moment(applicable_period_from).format("YYYY")
        }))
    }

    const handleGeneratePRN = (data) =>{
        const sssData = {
            tpa_id : tpaId,
            contribution_id : data.contribution,
            account_no : data.ssId,
            app_date_from : moment(data.applicablePeriodFrom).format("MMYYYY"),
            app_date_to : moment(data.applicablePeriodTo).format("MMYYYY"),
            membership_type_id : data.membershipType,
            wallet_type : walletType
        }
        dispatch(getPrnDataAction(sssData))
    }

    const closeErrorAlert = () => {
        setErrorData(null)
    }
    
    const formatPeso = (amount) => {
        return `₱ ${Number(amount).toLocaleString('en-PH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const redirectToSAO = () => {
        window.open(prnDataGenerate.soa, '_blank', 'noopener,noreferrer')
    }

    const continueToPayment = () =>{
        handleOnCloseModal(prnDataGenerate);
        resetForm()
        setOpenModal(false)
        setErrorData(null)
    }

    return (
        <Dialog
                disableBackdropClick
                open={openModal}
                classes={{ paper: !prnDataGenerate ? styles.dialogPaper : styles.dialogPaperPRN }}
        >
            {!prnDataGenerate ? 
            <>
                <DialogTitle disableTypography className={styles.dialogTitle}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography className={styles.header}>
                            Generate Payment Reference Number
                        </Typography>
                        <IconButton onClick={closeModalAndReset}>
                            <CloseIcon fontSize="large"/>
                        </IconButton>
                    </Box>
                </DialogTitle>
                <br/>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2} direction="row" justify="flex-start" alignItems="flex-start">
                        {errorData && (
                            <Alert
                                variant="filled"
                                severity="error"
                                icon={<BlockIcon fontSize={"inherit"} />}
                                onClose={closeErrorAlert}
                            >
                                {errorData.errorMessage}
                            </Alert>
                        )}
                        <Grid item xs={12}>
                            <Typography className={styles.body}>
                                Payment Information
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="stretch">
                                <TextField fullWidth size="small" variant="outlined" 
                                    label={<Typography className={styles.inputLabel}>SS No.</Typography>}
                                    name="ssId"
                                    value={formik.values.ssId}
                                    disabled={isLoading && requestType === "PRN_DATA"}
                                    onChange={(e) => {
                                        setErrorData(null);
                                        const value = e.target.value;
                                        // Only allow digits
                                        if (/^\d*$/.test(value)) {
                                            formik.setFieldTouched('ssId', true);
                                            formik.handleChange(e);
                                        }
                                    }}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    error={formik.touched.ssId && Boolean(formik.errors.ssId)}
                                    helperText={formik.touched.ssId && formik.errors.ssId}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="stretch">
                                <TextField
                                    size="small"
                                    select={true}
                                    fullWidth
                                    label={<Typography className={styles.inputLabel}>Payment Type</Typography>}
                                    variant="outlined"
                                    name="membershipType"
                                    value={formik.values.membershipType}
                                    onChange={async (e)=>{
                                        setErrorData(null);
                                        formik.handleChange(e)
                                        await formik.setFieldValue('contribution', '');
                                        await formik.validateForm();

                                        if (formik.values.applicablePeriodFrom) {
                                            const errors = await formik.validateForm();
                                            if (!errors.applicablePeriodFrom) {
                                                setContributionOptions([]);
                                                handleContributions(formik.values.applicablePeriodFrom, e.target.value);
                                            }
                                        }
                                    }}
                                    error={formik.touched.membershipType && Boolean(formik.errors.membershipType)}
                                    helperText={formik.touched.membershipType && formik.errors.membershipType}
                                    disabled={isLoading && (requestType === "MEMBERSHIP_TYPE" || requestType === "PRN_DATA")}
                                    InputProps={{
                                        startAdornment: isLoading && requestType === "MEMBERSHIP_TYPE" ? (
                                            <CircularProgress color="secondary" size={20} />
                                        ) : null,
                                    }}
                                >
                                    {membershipTypeOptions.map((option, i) => (
                                        <MenuItem key={i} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="stretch">
                                <KeyboardDatePicker
                                    size="small"
                                    disableToolbar
                                    variant="outlined"
                                    inputVariant="outlined"
                                    fullWidth
                                    format={"MMMM yyyy"}
                                    views={['year', 'month']}
                                    id="applicable_period_from"
                                    disabled={isLoading && requestType === "PRN_DATA"}
                                    label={<Typography className={styles.inputLabel}>Applicable Period (From)</Typography>}
                                    value={formik.values.applicablePeriodFrom}
                                    onChange={async (value) => {
                                        setErrorData(null);
                                        await formik.setFieldValue('contribution', '');
                                        await formik.setFieldTouched('applicablePeriodFrom', true); // mark as touched
                                        await formik.setFieldValue('applicablePeriodFrom', value)

                                        // Option 1: Use validateForm to ensure latest errors
                                        const errors = await formik.validateForm();
                                        if (!errors.applicablePeriodFrom && formik.values.membershipType) {
                                            setContributionOptions([]);
                                            handleContributions(value, formik.values.membershipType);
                                        }else{
                                            setContributionOptions([]);
                                        }
                                    }}
                                    inputValue={formik.values.applicablePeriodFrom?._i}
                                    error={formik.touched.applicablePeriodFrom && Boolean(formik.errors.applicablePeriodFrom)}
                                    helperText={formik.touched.applicablePeriodFrom && formik.errors.applicablePeriodFrom}
                                    KeyboardButtonProps={{
                                        'aria-label': 'change date',
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="stretch">
                                <KeyboardDatePicker
                                    size="small"
                                    disableToolbar
                                    variant="outlined"
                                    inputVariant="outlined"
                                    fullWidth
                                    format={"MMMM yyyy"}
                                    views={['year', 'month']}
                                    id="applicable_period_to"
                                    label={<Typography className={styles.inputLabel}>Applicable Period (To)</Typography>}
                                    value={formik.values.applicablePeriodTo}
                                    disabled={(isLoading && requestType === "PRN_DATA") || (formik.values.applicablePeriodFrom ? false : true)}
                                    onChange={(value) => {
                                        setErrorData(null);
                                        formik.setFieldTouched('applicablePeriodTo', true);
                                        formik.setFieldValue('applicablePeriodTo', value);
                                    }}
                                    inputValue={formik.values.applicablePeriodTo?._i}
                                    error={formik.touched.applicablePeriodTo && Boolean(formik.errors.applicablePeriodTo)}
                                    helperText={formik.touched.applicablePeriodTo && formik.errors.applicablePeriodTo}
                                    //InputLabelProps={{ shrink: true }}
                                    KeyboardButtonProps={{
                                        'aria-label': 'change date',
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="stretch">
                                <TextField
                                    size="small"
                                    select={true}
                                    fullWidth
                                    label={<Typography className={styles.inputLabel}>Monthly Contribution</Typography>}
                                    variant="outlined"
                                    name="contribution"
                                    value={formik.values.contribution}
                                    onChange={(e)=>{
                                        setErrorData(null);
                                        formik.handleChange(e)
                                    }}
                                    error={formik.touched.contribution && Boolean(formik.errors.contribution)}
                                    helperText={!isLoading && formik.values.applicablePeriodFrom && formik.values.membershipType && contributionOptions.length === 0  ?
                                        "No contribution list found for the selected period." : 
                                        formik.touched.contribution && formik.errors.contribution}
                                    disabled={isLoading && (requestType === "CONTRIBUTIONS" || requestType === "PRN_DATA") || !formik.values.applicablePeriodFrom || !formik.values.membershipType}
                                    InputProps={{
                                        startAdornment: isLoading && requestType === "CONTRIBUTIONS" ? (
                                            <CircularProgress color="primary" size={20} />
                                        ) : null,
                                    }}
                                >
                                    {contributionOptions.map((option, i) => (
                                        <MenuItem key={i} value={option.id} data-name={option.year}>
                                            {formatPeso(option.amount)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography className={styles.subtitle}>
                                Reminder: The new SS amount will only be applicable from January {storedLatestValidFrom || moment().year()} onwards.
                            </Typography>
                        </Grid>
                    </Grid>
                    <br/>
                    <DialogActions className={styles.dialogActions}>
                        <Button
                            variant="outlined"
                            className={styles.buttonGeneratePRN}
                            type="submit"
                            disabled={!(formik.isValid && formik.dirty) || (isLoading && requestType === "PRN_DATA")}
                            //disabled={isLoading && requestType === "PRN_DATA"}
                        >
                            {/* <span className={isLoading && requestType === "PRN_DATA" ? styles.bounce : ""}>
                                Generate PRN
                            </span> */}
                            {isLoading && requestType === "PRN_DATA" ? <CircularProgress size={21} style={{color: "#CED4DA", marginLeft: 2}} /> : "Generate PRN"}
                        </Button>
                    </DialogActions>
                </form>
            </> : 
            <>
                <Grid container direction="column" justify="center" alignItems="center">
                    <Grid item xs={12} >
                        <CheckCircleOutline fontSize="large" className={styles.checkCircle}/>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography className={styles.prnHeader}>
                            Payment Reference No.
                        </Typography>
                    </Grid>
                    <br/>
                    <Grid item xs={12} style={{width:'80%'}}>
                        <Box className={styles.prnNumberBox}>
                            <Typography className={styles.prnNumber}>
                                {prnDataGenerate.prn}
                            </Typography>
                        </Box>
                    </Grid>
                    <br/>
                    <Grid item xs={12} style={{width:'80%'}}>
                        <Grid container direction="row" justify="space-between" alignItems="center">
                            <Grid item xs={10}>
                                <Typography className={styles.header}>
                                    Total Contribution : {formatPeso(prnDataGenerate.amount)}
                                </Typography>
                                <label className={styles.body}>
                                    {moment(prnDataGenerate.applicableDateFrom, "MMYYYY").format("MMMM yyyy")} to {moment(prnDataGenerate.applicableDateTo, "MMYYYY").format("MMMM yyyy")}
                                </label>
                            </Grid>
                            <Grid item xs={2}>
                                <IconButton onClick={redirectToSAO} className={styles.closeButton}>
                                    <img src={pdfIcon} className={styles.pdfIcon}/>
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                    <br/>
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            className={styles.buttonContinuePayment}
                            onClick={continueToPayment}
                        >
                            Continue to Payment
                        </Button>
                    </Grid>
                    <br/>
                </Grid>
            </>}
            
        </Dialog>
    )
}

export default GeneratePRNModal